import { useEffect, useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';

import * as Sentry from '@sentry/react'; // eslint-disable-line import/no-namespace

import queryString from 'core/libs/query-string';
import { withRouter } from 'core/libs/router';

import { unityId as unityIdApi } from 'core/api/definitions/unityId';

import withBreakpoint from 'core/hocs/withBreakpoint';

import loadScript from 'core/utils/load-script';

import UnityIdContext from './';

import {
  SDK_URL,
  DOMAIN_SBER_ID,
  CSS_SELECTOR_FOR_SDK,
  UNITY_REDIRECT_URI_LS_LEY,
} from './constants';

import {
  checkOnPauseSSOSberId,
  setOnPauseSSOSberId,
  getFilteredSearchParams,
} from './utils';


function UnityIdProvider({ location, history, children, isMobile }) {
  const searchParams = queryString.parse(location.search) || {};
  const { code, state } = searchParams;

  const filteredParams = getFilteredSearchParams(searchParams);

  const [oidcParams, setOidcParams] = useState(null);
  const [userInfo, setUserInfo] = useState(null);
  const [sdk, setSdk] = useState(null);

  /**
   * Получаем данные для инициализации SberId JS SDK
   */
  const getOidcParams = async () => {
    try {
      const data = await unityIdApi.init();
      setOidcParams(data?.attributes);
    } catch (e) {
      console.warn('unityIdApi.init:', e);
    }
  };

  /**
   * После аутентификации нас редиректит обратно на сайт.
   * В этот момент нам доступны code и state.
   * Отправляем запрос на ручку sign_in.
   * В ответ нам сразу приходят данные о пользователе.
   * Кладем их в UI state.
   */
  const signIn = async params => {
    try {
      const info = await unityIdApi.signIn({ code: params.code, state: params.state });
      setUserInfo(info);
    } catch (e) {
      console.warn('unityIdApi.signIn:', e);
    }
  };

  /**
   * Запрашиваем данные о пользователе из нашего backend.
   */
  const getInfo = async () => {
    try {
      const info = await unityIdApi.getInfo();
      setUserInfo(info);
      return info;
    } catch (e) {
      /**
       * Если куки user_id нет, то пользователь Unauthorized.
       * Отправляем проходить авторизацию руками, по кнопке.
       * Нечего мусорить консоль.
       */
      if (e.statusCode === 401) return null;

      console.warn('unityIdApi.getInfo:', e);
      return null;
    }
  };

  const signOut = useCallback(async () => {
    setOnPauseSSOSberId();
    try {
      await unityIdApi.signOut();
      setUserInfo(null);
    } catch (e) {
      console.warn('unityIdApi.signOut:', e);
    }
  }, []);

  /**
   * Инициализация SberId JS SDK.
   * Необходимы oidcParams.
   */
  const initSDK = useCallback(async () => {
    try {
      const sberSdk = await new window.SberidSDK({
        baseUrl: `https://${DOMAIN_SBER_ID}`,
        oidc: oidcParams,
        container: '.' + CSS_SELECTOR_FOR_SDK,
        buttonProps: {
          size: 'sm',
          stretched: true,
          mainText: isMobile ? 'login' : 'default',
        },
        notification: {
          enable: false,
        },
        fastLogin: {
          enable: !checkOnPauseSSOSberId(),
          mode: 'auto',
          onErrorAutoMode: () => {
            // eslint-disable-next-line no-console
            console.error('Ошибка автологина ¯\\_(ツ)_/¯');
          },
        },
        onSuccessCallback: async ({ state: autoState, code: autoCode } = {}) => {
          if (autoState && autoCode) {
            await signIn({ state: autoState, code: autoCode });
          }
        },
        onErrorCallback: async error => {
          console.error('Что-то пошло не так: ', error);
          Sentry.captureException(error);
        },
      }).init();
      setSdk(sberSdk);
    } catch (e) {
      console.warn('Fail init SberidSDK', e);
    }
  }, [isMobile, oidcParams]);

  useEffect(() => {
    if (!sdk) return;

    /**
     * Чтобы сохранить путь, откуда пользователь запросил авторизацию, по документации сбера
     * мы могли бы сохранить его как GET параметры в redirect_uri ?utm_source=path или ?option1=path
     * Но ограничение размера параметра в 50 символов нам не подходит.
     * URLs наших топиков, как правило, длиннее.
     * По этой причине храним путь в localStorage
     */
    localStorage.setItem(UNITY_REDIRECT_URI_LS_LEY, location.pathname);

    /**
     * Из разрешенных utm_ меток, формируем строку
     */
    const redirectString = queryString.stringify(filteredParams);

    /**
     * На каждое обновления URL сайта актуализируем oidcParams.redirect_uri
     */
    sdk.setOIDCParams({
      ...oidcParams,
      redirect_uri: `${oidcParams.redirect_uri}?${redirectString}`,
    });
  }, [sdk, oidcParams, filteredParams, location.pathname]);

  /**
   * Если не сработал авто вход, можем пройти авторизацию вручную.
   * После аутентификации по кнопке "Войти по Сбер ID" произойдет редирект к нам на площадку,
   * на главную страницу с GET-params /?code=bla123&state=bla123
   */
  useEffect(() => {
    if (!code || !state) return;

    signIn({ state, code });
  }, [code, state]);

  /**
   * После того как пользователь авторизовался у нас есть userInfo.
   * Теперь нам нужно убрать state и code из URL.
   * Производим редирект на страницу с которой пришел пользователь.
   * При этом сохраняя безопасные GET-params.
   */
  useEffect(() => {
    if (userInfo && code && state) {
      const path = localStorage.getItem(UNITY_REDIRECT_URI_LS_LEY) || '/';
      const searchString = queryString.stringify(filteredParams);

      history.replace({
        pathname: path,
        search: searchString,
      });
    }
  }, [code, state, userInfo, history, filteredParams]);

  useEffect(() => {
    const hasAuthenticationData = code && state;

    /**
     * Если данные о пользователе получены из ручки /sign_in
     * или происходит аутентификация по ручке sign_in ничего не делаем.
     */
    if (hasAuthenticationData || userInfo) return;

    (async function() {
      /**
       * Если пользователь был авторизован, данные о нем хранятся в нашем backend.
       * Запрашиваем их, кладем и выходим. SDK не нужно.
       */
      const info = await getInfo();

      if (info) return;

      /**
       * Если пользователь был авторизован с помощью автовхода SDK, а потом решил выйти,
       * заново инициализировать SDK не нужно.
       */
      if (window.SberidSDK) return;

      await getOidcParams();
    })();
  }, [userInfo, code, state]);

  /**
   * Если есть oidcParams для инициализации SberId JS SDK,
   * загружаем SDK скрипт и производим инициализацию.
   * Получем кнопку "Войти по Сбер ID".
   */
  useEffect(() => {
    if (!oidcParams || userInfo) return;

    if (window.SberidSDK) {
      initSDK();
      return;
    }

    loadScript(SDK_URL, 'SberidSDK')
      .then(initSDK)
      .catch(error => console.error(error));
  }, [oidcParams, initSDK, userInfo]);

  const contextValue = useMemo(() => ({
    userInfo,
    signOut,
    /**
     * Состояния: isAuthorized и resolved
     * нужны чтобы соответствовать схеме PrivateRoute
     */
    isAuthorized: !!userInfo,
    resolved: true,
  }), [userInfo, signOut]);

  return (
    <UnityIdContext.Provider value={contextValue}>
      {children}
    </UnityIdContext.Provider>
  );
}

UnityIdProvider.propTypes = {
  location: PropTypes.object,
  history: PropTypes.object,
  isMobile: PropTypes.bool,
};

export default withRouter(withBreakpoint(UnityIdProvider));
